import App from "@/App.vue";
import Bus from "@/utils/bus";
import * as Sentry from "@sentry/vue";
import Vue from "vue";
import { isTimeout, withTimeout } from "@/utils/with-timeout";
import { reportRestoreError } from "@/utils/report-restore-error";

const RESTORE_CLUB_TIMEOUT_MS = 4000;

Vue.config.productionTip = false;
Vue.config.silent = true;

let appVue;
let isSentryInitialized = false;

const loadBasicPlugins = async () => {
  // Load the basic plugins
  const plugins = await import("@/plugins");

  // Attendre que la locale initiale soit chargée
  try {
    await plugins.localeReady;
  } catch (e) {
    console.error("Failed to load initial locale, falling back to 'fr':", e);
    plugins.default.i18n.locale = "fr";
  }

  return plugins;
};

const loadAdditionalPlugins = async () => {
  let plugins;

  // Load the basic plugins
  await import("@/plugins/custom").then((obj) => {
    plugins = obj;
  });

  await import("@/filters");
  await import("@/directives");

  return plugins;
};

const initApp = async (basicPlugins, additionalPlugins, loadedPlugins = false) => {
  return new Promise((resolve => {
    if (process.env.VUE_APP_SENTRY_DSN && ! isSentryInitialized) {
      Sentry.init({
        Vue,
        dsn: process.env.VUE_APP_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1,
        tracePropagationTargets: [/^https:\/\/v3-develop-api.doinsport\.club/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });

      isSentryInitialized = true;
    }
    if (! loadedPlugins) {
      appVue = new Vue({
        ...basicPlugins.default,
        render: (h) => h(App),
      });
    } else {
      appVue = new Vue({
        ...basicPlugins.default,
        ...additionalPlugins.default,
        render: (h) => h(App),
      });
    }
    appVue.$mount("#app");

    resolve(loadedPlugins);
  }));
};

const loadAppVue = async () => {
  const basicPlugins = await loadBasicPlugins();

  // Avant le montage : les écrans lisent le club dès leur création, un club périmé ou réécrit
  // par le BO v4 leur resterait jusqu'à la reconnexion. Un échec ou une API trop lente gardent
  // le club mémorisé : le BO s'affiche quand même.
  let restoreError = null;
  // Passé à true au délai : la restauration, qui continue, sait alors que le BO est déjà monté.
  const startup = { late: false };

  if (localStorage.getItem("user") && localStorage.getItem("token")) {
    await withTimeout(basicPlugins.default.store.dispatch("auth/restoreClubSession", startup), RESTORE_CLUB_TIMEOUT_MS)
      .catch((error) => {
        startup.late = isTimeout(error);
        restoreError = error;
      });
  }

  const storedClub = JSON.parse(localStorage.getItem("current-club"));
  const loaded = storedClub ? await initApp(basicPlugins, await loadAdditionalPlugins(), true) : await initApp(basicPlugins, {}, false);

  // Sentry n'est initialisé que par initApp : signalé ici, l'échec ne passe pas inaperçu.
  if (restoreError && isSentryInitialized) {
    reportRestoreError(Sentry, restoreError);
  }

  return loaded;
};

loadAppVue();

Bus.$on("on:current-club-loaded", async () => {
  const basicPlugins = await loadBasicPlugins();
  const additionalPlugins = await loadAdditionalPlugins();

  appVue.$destroy();

  initApp(basicPlugins, additionalPlugins, true)
    .then((loadedPlugins) => {
      if (loadedPlugins) {
        if(appVue.$router.currentRoute.name !== "planning") {
          appVue.$router.replace("/");
        }
      }
    });
});